import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'general', label: 'General' },
  { id: 'requirements-gpu', label: 'Requirements & GPU' },
  { id: 'licensing-account', label: 'Licensing & account' },
  { id: 'privacy', label: 'Privacy & your footage' },
  { id: 'storage-files', label: 'Storage & files' },
  { id: 'search-analysis', label: 'Search & analysis' },
  { id: 'premiere-timelines', label: 'Premiere & timelines' },
  { id: 'agents', label: 'AI agents (MCP)' },
];

// One question + stable-anchor answer. Every <Q id> is a real permalink target
// (linkable as /docs/faq#id) independent of the section-level `toc` ids above.
function Q({ id, q, children }: { id: string; q: string; children: ReactNode }) {
  return (
    <div id={id} style={{ scrollMarginTop: 110, marginBottom: 22 }}>
      <h3 style={{ margin: '0 0 6px', fontFamily: 'var(--font-display)', fontSize: 15.5, fontWeight: 600, letterSpacing: 'var(--tracking-heading)', color: 'var(--fg-1)' }}>
        {q}
      </h3>
      {children}
    </div>
  );
}

export default function Body() {
  return (
    <>
      <DocSection id="general" title="General">
        <Q id="what-is-clipradar" q="What is ClipRadar?">
          <p>
            ClipRadar is a Windows desktop app that searches your video and image footage by what&apos;s on
            screen, what&apos;s said, and who appears in it — everything runs locally on your machine (analysis
            needs an NVIDIA GPU), nothing uploaded. See{' '}
            <Link to="/docs/concepts/how-it-works">How ClipRadar works</Link> for the full picture.
          </p>
        </Q>
        <Q id="other-editors-than-premiere" q="Does ClipRadar work with editors other than Premiere Pro?">
          <p>
            Live, driven timelines are Premiere Pro only — the <span style={kbd}>Premiere panel</span> has no
            equivalent for other NLEs. You can still get results into DaVinci Resolve or Final Cut by exporting
            a timeline XML and importing it there; that&apos;s a one-way import, not a live connection. See{' '}
            <Link to="/docs/guides/build-a-rough-cut">Build a rough cut</Link>.
          </p>
        </Q>
        <Q id="is-clipradar-free" q="Is ClipRadar free?">
          <p>
            You can try it free — the trial includes 40 searches, keyed to your email, no card required. After
            that you need a paid license to keep searching. See{' '}
            <Link to="/docs/concepts/licensing">Licensing &amp; activation</Link>.
          </p>
        </Q>
        <Q id="where-do-i-download-clipradar" q="Where do I download ClipRadar?">
          <p>
            From the <Link to="/download">Download page</Link>. It&apos;s a small web installer that pulls the
            full app package during setup — Windows 10/11, 64-bit only. Walkthrough:{' '}
            <Link to="/docs/install">Download &amp; install</Link>.
          </p>
        </Q>
      </DocSection>

      <DocSection id="requirements-gpu" title="Requirements & GPU">
        <Q id="gpu-required" q="Does ClipRadar need a GPU?">
          <p>
            Yes. An <strong>NVIDIA GPU with CUDA support is required</strong> — ClipRadar does not run without
            one, and there is no supported CPU mode. Full breakdown:{' '}
            <Link to="/docs/reference/system-requirements">System requirements</Link>.
          </p>
        </Q>
        <Q id="how-much-vram-do-i-need" q="How much VRAM do I need?">
          <p>
            The practical thing that matters is <strong>free VRAM</strong>, not just having a GPU at all —
            ClipRadar sizes its analysis batches to whatever memory is actually free at the moment (adapting
            down automatically on smaller cards), and warns you if a card is too tight rather than failing
            silently. See <Link to="/docs/concepts/models-and-your-gpu">AI models &amp; your GPU</Link> and{' '}
            <Link to="/docs/reference/system-requirements">System requirements</Link> for recommended minimums.
          </p>
          <DocShot
            slug="faq"
            shot="compute-device"
            alt="Settings > System group showing the Compute device readout (CUDA GPU) and app version"
            caption="Settings → System shows exactly which compute device ClipRadar detected."
          />
        </Q>
        <Q id="amd-or-integrated-graphics" q="Can I run ClipRadar on an AMD or integrated GPU?">
          <p>
            No. ClipRadar requires an NVIDIA GPU with CUDA support — AMD and Intel GPUs, and CPU-only machines,
            aren&apos;t supported today. If Settings shows no GPU detected, see{' '}
            <Link to="/docs/troubleshooting/gpu-not-detected">ClipRadar can&apos;t find my GPU</Link>.
          </p>
        </Q>
        <Q id="shared-gpu-with-premiere" q="Can I run ClipRadar and Premiere Pro on the GPU at the same time?">
          <p>
            Yes — ClipRadar checks live free VRAM (not total VRAM) before sizing its work, specifically because
            editors run Premiere or After Effects alongside it. On a tight card this can mean smaller batches
            or a slowdown warning rather than a crash. See{' '}
            <Link to="/docs/concepts/models-and-your-gpu">AI models &amp; your GPU</Link>.
          </p>
        </Q>
      </DocSection>

      <DocSection id="licensing-account" title="Licensing & account">
        <Q id="one-machine-or-two" q="How many machines can I activate my license on?">
          <p>
            A free trial runs on <strong>one machine</strong>; a paid license runs on{' '}
            <strong>two machines</strong> at once — for example your desktop and your laptop. To use it
            on a machine beyond those seats, deactivate one first. If you try to activate past your
            limit you&apos;ll see &quot;This license is already activated on another device&quot; — see{' '}
            <em>Move my license to a new PC</em> below.
          </p>
        </Q>
        <Q id="trial-runs-out" q="What happens when my free trial runs out?">
          <p>
            Search is blocked, not broken — you&apos;ll see a license dialog explaining your searches are used
            up, with a path to upgrade. Nothing you&apos;ve already indexed is lost. See{' '}
            <Link to="/docs/concepts/licensing">Licensing &amp; activation</Link>.
          </p>
        </Q>
        <Q id="works-offline" q="Does ClipRadar work offline?">
          <p>
            Mostly, yes. Activating a license needs internet once; after that it keeps working offline for a
            few days on a recently-validated license before it needs to reconnect and re-check. See{' '}
            <Link to="/docs/concepts/licensing">Licensing &amp; activation</Link>.
          </p>
        </Q>
        <Q id="move-license-new-pc" q="How do I move my license to a new PC?">
          <p>
            In <span style={kbd}>Settings → Licensing</span> on the old machine, click <span style={kbd}>Deactivate</span> to
            free the seat, then activate the same key on the new machine. See{' '}
            <Link to="/docs/interface/settings">The Settings tab</Link>.
          </p>
        </Q>
      </DocSection>

      <DocSection id="privacy" title="Privacy & your footage">
        <Q id="footage-leaves-computer" q="Does my footage ever leave my computer?">
          <p>
            No. Analysis and search run entirely on your machine — there is no cloud inference, and your footage
            never leaves your local disk. The only things ClipRadar sends over the network are license
            checks, update checks, one-time AI model downloads, and a bug report if you explicitly submit one.
            See <Link to="/docs/concepts/local-and-private">Local &amp; private</Link> for the exact list.
          </p>
        </Q>
        <Q id="bug-report-what-is-sent" q="What does a bug report actually send?">
          <p>
            Just what you type — a description and an optional email address. If you also check{' '}
            <span style={kbd}>Include diagnostics</span>, it attaches your app version, OS, GPU, and a handful of
            recent log lines, scrubbed of usernames and file paths. It never includes your footage, filenames,
            or file contents. See <Link to="/docs/get-help">Report a bug &amp; get help</Link>.
          </p>
        </Q>
        <Q id="turn-off-diagnostics" q="Can I turn off diagnostics and crash reporting?">
          <p>
            Yes — the <span style={kbd}>Send crash reports &amp; diagnostics</span> toggle in{' '}
            <span style={kbd}>Settings → Privacy &amp; diagnostics</span> controls background crash reporting and
            automatic diagnostic attachments. Turning it off doesn&apos;t stop you from filing a bug report by
            hand — it just means nothing is collected automatically.
          </p>
        </Q>
        <Q id="agent-sees-my-footage" q="If I connect an AI agent, can it see my footage?">
          <p>
            No — an agent sees metadata, transcripts, and thumbnails, never your raw video files. See{' '}
            <Link to="/docs/concepts/local-and-private">Local &amp; private</Link> and{' '}
            <Link to="/docs/concepts/agentic-editing">Agentic editing</Link>.
          </p>
        </Q>
      </DocSection>

      <DocSection id="storage-files" title="Storage & files">
        <Q id="where-is-my-data-stored" q="Where does ClipRadar store my data?">
          <p>
            Your original media stays exactly where it already is on disk — ClipRadar indexes files in place, it
            doesn&apos;t copy or move them. Everything ClipRadar generates (models, cache, license, logs) lives
            under a dedicated ClipRadar data folder. Full paths:{' '}
            <Link to="/docs/reference/where-your-data-lives">Where ClipRadar stores things</Link>.
          </p>
        </Q>
        <Q id="how-much-disk-space" q="How much disk space does ClipRadar use?">
          <p>
            The installer itself is tiny; it downloads the real app package (about 3.2 GB) during setup, plus
            roughly 13 GB of AI models on first run. After that, disk use grows with your library, since
            each analyzed source gets its own cache of thumbnails, embeddings, and transcripts. See{' '}
            <Link to="/docs/reference/system-requirements">System requirements</Link> for the recommended free
            space to keep available.
          </p>
        </Q>
        <Q id="move-cache-folder" q="Can I move the cache folder to a different drive?">
          <p>
            Yes — <span style={kbd}>Settings → Storage → Cache folder</span> lets you browse to a new location;
            ClipRadar prompts you to move the existing cache or leave it behind. See{' '}
            <Link to="/docs/interface/settings">The Settings tab</Link>.
          </p>
        </Q>
        <Q id="supported-file-formats" q="What file formats does ClipRadar support?">
          <p>
            Video: <span style={kbd}>.mp4</span> <span style={kbd}>.mkv</span> <span style={kbd}>.avi</span>{' '}
            <span style={kbd}>.mov</span> <span style={kbd}>.webm</span> <span style={kbd}>.mxf</span>{' '}
            <span style={kbd}>.mts</span>. Images: <span style={kbd}>.jpg</span> <span style={kbd}>.jpeg</span>{' '}
            <span style={kbd}>.png</span> <span style={kbd}>.webp</span> <span style={kbd}>.bmp</span>{' '}
            <span style={kbd}>.tif</span> <span style={kbd}>.tiff</span>. Camera-raw formats like BRAW, R3D, and
            ProRes RAW aren&apos;t supported. Full list:{' '}
            <Link to="/docs/reference/supported-formats">Supported formats</Link>.
          </p>
        </Q>
      </DocSection>

      <DocSection id="search-analysis" title="Search & analysis">
        <Q id="analyze-before-search" q="Do I have to analyze footage before I can search it?">
          <p>
            Yes — ClipRadar has to run its analyze pass (scene detection, visual embeddings, speech
            transcription, face recognition) on your GPU once per file. After that, searching it is instant.
            See <Link to="/docs/concepts/analyzing-media">Analyzing your footage</Link>.
          </p>
        </Q>
        <Q id="visuals-speech-ultra-difference" q="What's the difference between Visuals, Speech, and Ultra Search?">
          <p>
            <strong>Visuals</strong> reads the pixels — describe what&apos;s on screen.{' '}
            <strong>Speech</strong> searches your transcripts — quote or describe what was said.{' '}
            <strong>Ultra Search</strong> reads one plain-language sentence and fuses every signal — people,
            words, visuals, framing — into one ranked result. See{' '}
            <Link to="/docs/concepts/visual-search">Visual search</Link>,{' '}
            <Link to="/docs/concepts/speech-search">Speech search</Link>, and{' '}
            <Link to="/docs/concepts/ultra-search">Ultra Search</Link>.
          </p>
        </Q>
        <Q id="zero-search-results" q="Why am I getting zero search results?">
          <p>
            Usually one of: the footage isn&apos;t analyzed yet, it&apos;s excluded from search, your license
            is blocking search, or the query is too narrow. Walk through the checks in{' '}
            <Link to="/docs/troubleshooting/cant-search">I can&apos;t search</Link>.
          </p>
        </Q>
        <Q id="ultra-search-any-gpu" q="Does Ultra Search work on any GPU?">
          <p>
            Not identically. Ultra Search runs a local language model to parse your query, and that model&apos;s
            behavior is VRAM-tiered: cards under roughly 12 GB get a lighter, faster{' '}
            <span style={kbd}>Lite</span> model with reduced accuracy, and under heavy VRAM pressure Ultra falls
            back to plain search with a visible notice — never silently. See{' '}
            <Link to="/docs/concepts/ultra-search">Ultra Search</Link>.
          </p>
        </Q>
        <Q id="does-clipradar-know-who-is-speaking" q="Does ClipRadar know who is speaking in the transcript?">
          <p>
            No — ClipRadar doesn&apos;t identify speakers within a transcript (no speaker diarization). Speech
            search finds spoken words, and the separate People/Faces system recognizes and names faces on
            screen; they aren&apos;t linked to each other. See{' '}
            <Link to="/docs/concepts/speech-search">Speech search</Link> and{' '}
            <Link to="/docs/concepts/people-and-faces">People &amp; faces</Link>.
          </p>
        </Q>
      </DocSection>

      <DocSection id="premiere-timelines" title="Premiere & timelines">
        <Q id="need-panel-to-use-clipradar" q="Do I need the Premiere panel to use ClipRadar?">
          <p>
            No — ClipRadar is a full standalone desktop app on its own. The{' '}
            <span style={kbd}>Premiere panel</span> is optional: it&apos;s the same app docked inside Premiere Pro
            for editors who want it there. See{' '}
            <Link to="/docs/platforms/standalone-app">The standalone app</Link> and{' '}
            <Link to="/docs/platforms/premiere-panel">The Premiere panel</Link>.
          </p>
        </Q>
        <Q id="premiere-version-required" q="Which Premiere Pro version does the panel require?">
          <p>
            Premiere Pro 22.3 or newer. See{' '}
            <Link to="/docs/platforms/premiere-panel">The Premiere panel</Link>.
          </p>
        </Q>
        <Q id="resolve-final-cut-support" q="Can I use ClipRadar with DaVinci Resolve or Final Cut?">
          <p>
            Not live. Premiere Pro is the only editor ClipRadar drives directly. For Resolve or Final Cut,
            export a timeline XML from your search results and import it there — it&apos;s a one-way handoff,
            not a live connection. See <Link to="/docs/guides/build-a-rough-cut">Build a rough cut</Link>.
          </p>
        </Q>
        <Q id="panel-wont-open" q="Why won't the Premiere panel open?">
          <p>
            The most common cause is that the ClipRadar desktop app isn&apos;t running — it can be minimized to
            the tray, but it has to be running. Open it, then use{' '}
            <span style={kbd}>Window → ClipRadar</span> in Premiere and click{' '}
            <span style={kbd}>Reconnect</span> if you started ClipRadar after opening Premiere. Full checklist:{' '}
            <Link to="/docs/troubleshooting/panel-wont-open">The Premiere panel won&apos;t open or is blank</Link>.
          </p>
        </Q>
        <Q id="add-to-timeline-overwrite" q="Will adding a result to my timeline overwrite my edit?">
          <p>
            Not by default. The panel&apos;s placement mode controls this: <span style={kbd}>Smart</span> (the
            default) finds a free track and never overwrites existing clips, <span style={kbd}>Insert</span>{' '}
            ripples everything at the playhead, and <span style={kbd}>Append</span> adds to the end of the
            sequence. Pick one and it applies to every clip you add. See{' '}
            <Link to="/docs/platforms/premiere-panel">The Premiere panel</Link>.
          </p>
        </Q>
      </DocSection>

      <DocSection id="agents" title="AI agents (MCP)">
        <Q id="what-is-agent-mcp" q="What is Agent (MCP)?">
          <p>
            ClipRadar ships a local <strong>Model Context Protocol</strong> server that lets an AI agent (for
            example Claude Code or Claude Desktop) search your library and drive editing on your behalf. This
            is the only programmatic surface ClipRadar exposes — there is no public REST API. See{' '}
            <Link to="/docs/concepts/agentic-editing">Agentic editing</Link> and{' '}
            <Link to="/docs/reference/mcp-tools">MCP tools reference</Link>.
          </p>
        </Q>
        <Q id="connect-agent-to-clipradar" q="How do I connect an agent to ClipRadar?">
          <p>
            <span style={kbd}>Settings → Agent (MCP)</span> shows a live connection status and a copyable one-time
            connect command for your agent. The server only listens locally — nothing about the connection
            leaves your machine. See <Link to="/docs/interface/settings">The Settings tab</Link>.
          </p>
        </Q>
        <Q id="agent-edit-timeline-live" q="Can an agent edit my Premiere timeline automatically?">
          <p>
            Yes, when the <Link to="/docs/platforms/premiere-panel">Premiere panel</Link> is open and connected
            to your project, an agent can build a new sequence or place clips directly onto it. Without the
            panel connected, it can still hand you a timeline XML to import by hand. Either way, it proposes a
            plan and waits for your go-ahead before acting. See{' '}
            <Link to="/docs/guides/agentic-editing-quickstart">Agentic editing quickstart</Link>.
          </p>
        </Q>
        <Q id="agent-limits-and-safety" q="What can't an agent do?">
          <p>
            It can&apos;t modify your original media files, and its exports are sandboxed to a dedicated export
            folder rather than writing anywhere on disk. It doesn&apos;t see your raw footage — see{' '}
            <em>If I connect an AI agent, can it see my footage?</em> above — and it always presents a plan
            before searching, exporting, or touching a timeline, rather than acting silently.
          </p>
        </Q>
      </DocSection>
    </>
  );
}
